import { useMemo } from 'react';
import { createApiClient } from '@/api/client';
import { createProductIntroApi } from '@/api/product-intro';
import { useDebounce } from './useDebounce';
import { useProductIntro } from './useProductIntro';
import { ResultNoMatch } from './ResultNoMatch';
import { openInNewTab } from '@/utils/url-template';
import { ECHO_STORAGE_KEY } from '@/chat/echo-handoff';
import type { OverviewProps, OverviewEchoContext } from '@/types/widget';

export function OverviewWidget(props: OverviewProps): JSX.Element | null {
  const locale = props.locale ?? 'fr';
  // 200ms feels responsive but still avoids the keystroke-per-letter
  // backend storm — host can override via props.debounceMs.
  const debounceMs = props.debounceMs ?? 200;

  const api = useMemo(
    () =>
      createProductIntroApi(
        createApiClient({ baseUrl: props.apiBaseUrl, apiKey: props.apiKey }),
      ),
    [props.apiBaseUrl, props.apiKey],
  );

  const debouncedQuery = useDebounce(props.query, debounceMs);
  const state = useProductIntro(api, debouncedQuery, locale);

  function navigateToChat() {
    if (props.onOpenChat) {
      props.onOpenChat();
    } else if (props.chatPageUrl) {
      openInNewTab(props.chatPageUrl, true);
      // The new tab got its own copy of the echo; drop ours so a later chat
      // in this tab doesn't replay it.
      try {
        sessionStorage.removeItem(ECHO_STORAGE_KEY);
      } catch {
        // sessionStorage unavailable — nothing to drop
      }
    } else {
      console.warn(
        '[CalebasseChatbot] mountOverview: neither chatPageUrl nor onOpenChat provided',
      );
    }
  }

  function handleOpenChat() {
    if (state.status === 'no-match') {
      const echo: OverviewEchoContext = {
        query: debouncedQuery,
        result: state.result,
      };
      try {
        sessionStorage.setItem(ECHO_STORAGE_KEY, JSON.stringify(echo));
      } catch {
        // sessionStorage unavailable — degrade silently
      }
    }
    navigateToChat();
  }

  // Overview only surfaces itself to funnel users to chat when the query
  // has no product match — the host site's own search results carry the
  // matched-product path. `idle` / `loading` / `match` / `error` all
  // render nothing (no skeleton, no card, no flicker), so the widget is
  // invisible unless it has something actionable to offer.
  if (state.status !== 'no-match') return null;

  return (
    <div className="calebasse-chatbot-root cal-fragment">
      <ResultNoMatch
        locale={locale}
        onOpenChat={handleOpenChat}
        query={debouncedQuery}
      />
    </div>
  );
}
